import DeckGL from 'deck.gl';
import React, { useState, useEffect } from 'react';
import { Map } from 'react-map-gl';
import { GeoJsonLayer } from '@deck.gl/layers';
import { IconButton, Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { gray } from 'd3-color';

const PALE_BLUE   = [209, 229, 240, 255]; // >5 years ago
const LIGHT_BLUE  = [146, 197, 222, 255]; // ~4–5 years ago
const MID_BLUE    = [ 67, 147, 195, 255]; // ~2–3 years ago
const DARK_BLUE   = [ 33, 102, 172, 255]; // current year
const NEWEST_BLUE = [  5,  48,  97, 255]; // current year
const TRANSPARENT = [  0,   0,   0,   0]; // future

export default function GenericDeckGLMap() {
  const years = [2000, 2005, 2010, 2015, 2020];
  const [selectedYear, setSelectedYear] = useState(2000);
  const [isPlaying, setIsPlaying] = useState(false);
  const [styleKey, setStyleKey] = useState("Streets");

  const makeRasterStyle = (urlTemplate, attribution = "") => ({
    version: 8,
    sources: {
      rastertiles: {
        type: "raster",
        tiles: [urlTemplate],
        tileSize: 256,
        attribution,
      },
    },
    layers: [
      {
        id: "rastertiles",
        type: "raster",
        source: "rastertiles",
      },
    ],
  });

    const BASEMAPS = {
    "Satellite": "https://raw.githubusercontent.com/go2garret/maps/main/src/assets/json/arcgis_hybrid.json",
    "Light Mode": makeRasterStyle(
      "https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png",
      '© OpenStreetMap contributors © CARTO'
    ),
    "Dark Mode": makeRasterStyle(
      "https://basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}.png",
      '© OpenStreetMap contributors © CARTO'
    ),
     "Streets": makeRasterStyle(
      "https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png",
      '© OpenStreetMap contributors © CARTO'
    ),
    "Open Street Map": makeRasterStyle(
      "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      '© OpenStreetMap contributors'
    )
  };


  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setSelectedYear(prev => {
        const nextIndex = (years.indexOf(prev) + 1) % years.length;
        return years[nextIndex];
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [isPlaying]);

  const getFillColor = feature => {
    const year = feature.properties.year;

    if (year === 2000) {
      return selectedYear >= 2000 ? PALE_BLUE : TRANSPARENT;
    } else if (year >= 2001 && year <= 2005) {
      return selectedYear >= 2005 ? LIGHT_BLUE : TRANSPARENT;
    } else if (year >= 2006 && year <= 2010) {
      return selectedYear >= 2010 ? MID_BLUE : TRANSPARENT;
    } else if (year >= 2011 && year <= 2015) {
      return selectedYear >= 2015 ? DARK_BLUE : TRANSPARENT;
    } else if (year >= 2016 && year <= 2020) {
      return selectedYear >= 2020 ? NEWEST_BLUE : TRANSPARENT;
    } else {
      return TRANSPARENT;
    }
  };
  const layers = [
    new GeoJsonLayer({
      id: 'binary-pixels',
      data: '../data/geojson/binary_pixels.geojson',
      stroked: false,
      filled: true,
      getFillColor: getFillColor,
      updateTriggers: {
        getFillColor: selectedYear
      }
    })
  ];

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* Legend overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          background: 'rgba(255,255,255,0.95)',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          p: '12px 16px',
          zIndex: 2,
          minWidth: 160,
          textColor: gray(800),
          color: gray(800),
        }}
      >
        <Box sx={{ fontWeight: 600, mb: 1, fontSize: 14, color: "#000000" }}>Year of Urbanization</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
          <Box
            sx={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: 1,
              background: `rgba(${PALE_BLUE.join(',')})`,
              mr: 1,
              border: '1px solid #ccc'
            }}
          />
          <Box sx={{ fontSize: 13, color: "#000000" }}>{'2000'}</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
          <Box
            sx={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: 1,
              background: `rgba(${LIGHT_BLUE.join(',')})`,
              mr: 1,
              border: '1px solid #ccc'
            }}
          />
          <Box sx={{ fontSize: 13, color: "#000000" }}>{'2001-2005'}</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
          <Box
            sx={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: 1,
              background: `rgba(${MID_BLUE.join(',')})`,
              mr: 1,
              border: '1px solid #ccc'
            }}
          />
          <Box sx={{ fontSize: 13, color: "#000000" }}>{'2006-2010'}</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
          <Box
            sx={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: 1,
              background: `rgba(${DARK_BLUE.join(',')})`,
              mr: 1,
              border: '1px solid #ccc'
            }}
          />
          <Box sx={{ fontSize: 13, color: "#000000" }}>{'2011-2015'}</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            sx={{
              display: 'inline-block',
              width: 18,
              height: 18,
              borderRadius: 1,
              background: `rgba(${NEWEST_BLUE.join(',')})`,
              mr: 1,
              color: '#000000',
              border: '1px solid #ccc'
            }}
          />
          <Box sx={{ fontSize: 13, color: "#000000" }}>{'2016-2020'}</Box>
        </Box>
      </Box>
      <div className="absolute left-3 top-3 z-10 text-black">
            <select
              value={styleKey}
              onChange={(e) => setStyleKey(e.target.value)}
              className="bg-white/90 backdrop-blur px-3 py-1.5 rounded-md text-sm shadow border border-black/10"
            >
              {Object.keys(BASEMAPS).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
      <DeckGL
        initialViewState={{
          latitude: 6.763,
          longitude: -58.055,
          zoom: 10,
          pitch: 0,
          bearing: 0
        }}
        controller={true}
        layers={layers}
      >
        <Map mapStyle={BASEMAPS[styleKey]} />
      </DeckGL>
      <div
        style={{
          background: 'rgba(255, 255, 255, 1)',
          height: '12vh',
          position: 'absolute',
          bottom: 20,
          width: '100%',
          transform: 'translateY(-7vh)',
          display: 'flex',
          alignItems: 'center',
          zIndex: 1
        }}
      >
        <IconButton onClick={() => setIsPlaying(p => !p)} style={{ color: '#92843B' }}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>

        <div style={{ flexGrow: 1, margin: '0 12px' }}>
          <div style={{ position: 'relative', height: 4, background: 'rgba(200,200,200,0.5)', borderRadius: 2 }}>
            <div
              style={{
                position: 'absolute',
                left: `${(years.indexOf(selectedYear) / (years.length - 1)) * 100}%`,
                top: -6,
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: '#92843B',
                transform: 'translateX(-50%)'
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
            {years.map(year => (
              <span
                key={year}
                onClick={() => setSelectedYear(year)}
                style={{
                  cursor: 'pointer',
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: selectedYear === year ? '#92843B' : 'rgba(255,255,255,0.8)',
                  color: selectedYear === year ? '#fff' : '#333',
                  fontSize: 12
                }}
              >
                {year}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}